import _extends from 'babel-runtime/helpers/extends';
import _defineProperty from 'babel-runtime/helpers/defineProperty';
import _objectWithoutProperties from 'babel-runtime/helpers/objectWithoutProperties';
import _Map from 'babel-runtime/core-js/map';
import _slicedToArray from 'babel-runtime/helpers/slicedToArray';
import _Array$from from 'babel-runtime/core-js/array/from';
import _Object$getPrototypeOf from 'babel-runtime/core-js/object/get-prototype-of';
import _classCallCheck from 'babel-runtime/helpers/classCallCheck';
import _createClass from 'babel-runtime/helpers/createClass';
import _possibleConstructorReturn from 'babel-runtime/helpers/possibleConstructorReturn';
import _inherits from 'babel-runtime/helpers/inherits';
import _noop from 'lodash/noop';
import _throttle from 'lodash/throttle';
/* eslint-disable no-underscore-dangle */
import React, { PureComponent, Children } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import ThemeConsumer from '@sitebase/theme-context/es/ThemeConsumer';
import MediaConsumer from '@sitebase/media-context/es/MediaConsumer';

import { getAllowedAttributes } from '../props-filters';
import { category, makeOutfitParser } from '../outfit-parser';
import outfitPropValidator from '../outfitPropValidator';
import propTypeClassName from '../propTypeClassName';
import { Scrollable, calculateNextOffsetToLeft, calculateNextOffsetToRight, snapToThreshold, shiftThresholdToLeft, shiftThresholdToRight } from '../scrollable';
import CarouselItem from './CarouselItem';
import CarouselArrow from './CarouselArrow';
import * as style from './Carousel.style';

var kindsList = ['navigation', 'bare'];
var modifiersList = ['transparent', 'stretched'];
var parseOutfit = makeOutfitParser(category('kind', kindsList), category('modifier', modifiersList, true));

var addFadeMargin = function addFadeMargin(offset) {
  return Math.max(offset - 16, 0);
};

var shouldAlignToChild = function shouldAlignToChild(newOffset, outerWidth, innerWidth) {
  return !(newOffset + outerWidth >= innerWidth);
};

var Carousel = function (_PureComponent) {
  _inherits(Carousel, _PureComponent);

  function Carousel() {
    var _ref;

    var _temp, _this, _ret;

    _classCallCheck(this, Carousel);

    for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    return _ret = (_temp = (_this = _possibleConstructorReturn(this, (_ref = Carousel.__proto__ || _Object$getPrototypeOf(Carousel)).call.apply(_ref, [this].concat(args))), _this), _this.state = {
      hidden: true,
      overflow: 'both'
    }, _this._onLeftArrowClick = function (event) {
      _this.props.onLeftArrowClick(event);

      if (!_this._scrollable) return;
      var _this$_scrollable = _this._scrollable,
          scrollLeft = _this$_scrollable.scrollLeft,
          outerWidth = _this$_scrollable.outerWidth,
          innerWidth = _this$_scrollable.innerWidth;


      var thresholds = _this._childrenNodes.map(function (child) {
        return child.offsetLeft;
      });
      var shiftRight = function shiftRight(offset) {
        return shiftThresholdToRight(offset, innerWidth, outerWidth, thresholds);
      };
      var nextOffset = calculateNextOffsetToLeft(scrollLeft, outerWidth, innerWidth);

      if (nextOffset === 0) {
        _this._scrollable.scrollTo(addFadeMargin(snapToThreshold(nextOffset, thresholds)));
      } else {
        _this._scrollable.scrollTo(addFadeMargin(shiftRight(shiftRight(snapToThreshold(nextOffset, thresholds)))));
      }
    }, _this._onRightArrowClick = function (event) {
      _this.props.onRightArrowClick(event);

      if (!_this._scrollable) return;
      var _this$_scrollable2 = _this._scrollable,
          scrollLeft = _this$_scrollable2.scrollLeft,
          outerWidth = _this$_scrollable2.outerWidth,
          innerWidth = _this$_scrollable2.innerWidth;

      var nextOffset = calculateNextOffsetToRight(scrollLeft, outerWidth, innerWidth);

      if (shouldAlignToChild(nextOffset, outerWidth, innerWidth)) {
        var thresholds = _this._childrenNodes.map(function (child) {
          return child.offsetLeft;
        });
        _this._scrollable.scrollTo(addFadeMargin(snapToThreshold(nextOffset, thresholds)));
      } else {
        _this._scrollable.scrollTo(nextOffset);
      }
    }, _this._makeChildrenRefGetter = function (idx) {
      return function (el) {
        if (el) {
          _this._childrenNodesMap.set(idx, el);
        } else {
          _this._childrenNodesMap.delete(idx);
        }
      };
    }, _this._getScrollableRef = function (el) {
      _this._scrollable = el;
    }, _this._setOverflowState = function () {
      if (_this._scrollable) {
        var overflow = _this._scrollable.overflow;

        if (_this.state.overflow !== overflow) {
          _this.setState({ overflow: overflow });
        }
      }
    }, _this._setOverflowStateCallback = _throttle(_this._setOverflowState, 100, {
      trailing: true,
      leading: true
    }), _this._scrollToActive = function (_ref2) {
      var transition = _ref2.transition;

      if (!_this._scrollable) return;
      var activeElement = _this.props.activeElement;

      var activeElementOffset = _this._childrenNodesMap.get(activeElement) ? _this._childrenNodesMap.get(activeElement).offsetLeft : 0;
      var offsets = _this._childrenNodes.map(function (child) {
        return child.offsetLeft;
      });
      var nextOffset = addFadeMargin(shiftThresholdToLeft(snapToThreshold(activeElementOffset, offsets), _this._scrollable.innerWidth, _this._scrollable.outerWidth, offsets));
      if (transition) {
        _this._scrollable.scrollTo(nextOffset);
      } else {
        _this._scrollable.scrollLeft = nextOffset;
      }
    }, _this._convertChildrenMapToOrderedArray = function () {
      _this._childrenNodes = _Array$from(_this._childrenNodesMap.entries()).sort(function (_ref3, _ref4) {
        var _ref6 = _slicedToArray(_ref3, 1),
            elementOneIndex = _ref6[0];

        var _ref5 = _slicedToArray(_ref4, 1),
            elementTwoIndex = _ref5[0];

        return elementOneIndex - elementTwoIndex;
      }).map(function (entry) {
        return entry[1];
      });
    }, _this._childrenNodes = null, _this._childrenNodesMap = new _Map(), _this._scrollable = null, _this._renderCarousel = function (_ref7, theme) {
      var _cx, _cx2;

      var touchable = _ref7.touchable,
          device = _ref7.device;

      var desktopDevice = device === 'desktop';
      var _this$state = _this.state,
          overflow = _this$state.overflow,
          hidden = _this$state.hidden;

      var _this$props = _this.props,
          carouselChildren = _this$props.children,
          className = _this$props.className,
          activeElement = _this$props.activeElement,
          outfit = _this$props.outfit,
          restProps = _objectWithoutProperties(_this$props, ['children', 'className', 'activeElement', 'outfit']);

      var allowedAttributes = getAllowedAttributes(restProps);

      var arrowStyle = hidden ? { visibility: 'hidden' } : undefined;

      var parsedOutfit = parseOutfit(outfit);

      var carouselContainerClassName = cx(style.container, (_cx = {}, _defineProperty(_cx, style.containerMask, overflow === 'both'), _defineProperty(_cx, style.containerMaskLeft, overflow === 'left'), _defineProperty(_cx, style.containerMaskRight, overflow === 'right'), _cx));

      var themedClassName = cx(style.carousel, (_cx2 = {}, _defineProperty(_cx2, style.carouselTransparent, parsedOutfit.get('modifier').has('transparent')), _defineProperty(_cx2, style.carouselLight, theme === 'light'), _defineProperty(_cx2, style.carouselDark, theme === 'dark'), _defineProperty(_cx2, style.carouselStretch, parsedOutfit.get('modifier').has('stretched')), _defineProperty(_cx2, style.carouselMobile, !desktopDevice), _defineProperty(_cx2, style.carouselDesktop, desktopDevice), _cx2));

      var hasArrows = function hasArrows() {
        return Boolean((!touchable || desktopDevice) && overflow !== 'none');
      };

      return React.createElement(
        'div',
        _extends({}, allowedAttributes, { className: cx(themedClassName, className) }),
        React.createElement(
          'div',
          { className: carouselContainerClassName },
          React.createElement(
            Scrollable,
            { ref: _this._getScrollableRef, onScroll: _this._setOverflowStateCallback },
            Children.map(carouselChildren, function (child, idx) {
              return React.createElement(
                CarouselItem,
                {
                  innerRef: _this._makeChildrenRefGetter(idx),
                  active: activeElement === idx,
                  theme: theme,
                  key: idx,
                  'data-test-id': 'Carousel.item-' + idx,
                  bare: parsedOutfit.get('kind').has('bare')
                },
                child
              );
            })
          )
        ),
        hasArrows() && [React.createElement(CarouselArrow, {
          key: 'arrow-left',
          direction: 'left',
          'data-test-id': 'Carousel.arrow-left',
          onClick: _this._onLeftArrowClick,
          disabled: overflow === 'right',
          theme: theme,
          arrowStyle: arrowStyle
        }), React.createElement('div', { key: 'separator', className: style.separator, style: arrowStyle }), React.createElement(CarouselArrow, {
          key: 'arrow-right',
          direction: 'right',
          'data-test-id': 'Carousel.arrow-right',
          onClick: _this._onRightArrowClick,
          disabled: overflow === 'left',
          theme: theme,
          arrowStyle: arrowStyle
        })]
      );
    }, _temp), _possibleConstructorReturn(_this, _ret);
  }

  _createClass(Carousel, [{
    key: 'componentDidMount',
    value: function componentDidMount() {
      // Hide on first render to prevent flickering with SSR
      // eslint-disable-next-line react/no-did-mount-set-state
      this.setState({
        hidden: false
      });
      window.addEventListener('resize', this._setOverflowStateCallback);
      this.intervalId = setInterval(this._setOverflowStateCallback, 300);
      this._convertChildrenMapToOrderedArray();
      this._setOverflowState();
      this._scrollToActive({ transition: false });
    }
  }, {
    key: 'componentDidUpdate',
    value: function componentDidUpdate(_ref8) {
      var activeElement = _ref8.activeElement,
          children = _ref8.children;

      if (this.props.activeElement !== activeElement) this._scrollToActive({ transition: true });
      if (this.props.children !== children) this._convertChildrenMapToOrderedArray();
    }
  }, {
    key: 'componentWillUnmount',
    value: function componentWillUnmount() {
      window.removeEventListener('resize', this._setOverflowStateCallback);
      clearInterval(this.intervalId);
    }

    // eslint-disable-next-line complexity

  }, {
    key: 'render',
    value: function render() {
      var _this2 = this;

      return React.createElement(
        MediaConsumer,
        null,
        function (media) {
          return React.createElement(
            ThemeConsumer,
            null,
            function (theme) {
              return _this2._renderCarousel(media, theme);
            }
          );
        }
      );
    }
  }]);

  return Carousel;
}(PureComponent);

Carousel.propTypes = {
  /** The Carousel children */
  children: PropTypes.node,
  /**
   * Type: **string**.
   *
   * @see See [Commonly used attributes](#components-common-props) for a detailed description.
   */
  className: propTypeClassName,
  /** Index of selected element */
  activeElement: PropTypes.number,
  /**
   * Type: **string**.
   *
   * @see See [Commonly used attributes](#components-common-props) for a detailed description.
   *
   * Options:
   *
   * - Kinds:
   *   - "**navigation**",
   *   - "**bare**".
   * - Modifiers (multi choice):
   *   - "**transparent**",
   *   - "**stretched**" (have height 100%).
   */
  outfit: outfitPropValidator([].concat(kindsList, modifiersList)),
  /** The callback invoked once the right arrow button is clicked. */
  onRightArrowClick: PropTypes.func,
  /** The callback invoked once the left arrow button is clicked. */
  onLeftArrowClick: PropTypes.func
};
Carousel.defaultProps = {
  activeElement: 0,
  onRightArrowClick: _noop,
  onLeftArrowClick: _noop
};
export default Carousel;